import { useCallback, useRef } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import type { MonthData } from '~entities/index'

import { getTranslateX } from '~sharedLib'
import { HEADER_GAP, SLIDE_PADDING, TRANSLATE_THRESHOLD } from '../../constants'

type StickyUpdate = {
	titleEl: HTMLElement
	dividerEl: HTMLElement
	clampedOverscroll: number
	newDividerWidth: number
}

export const useStickyHeaders = (
	swiperRef: React.RefObject<SwiperType | null>,
	months: MonthData[],
	emitCurrentMonth: () => void
) => {
	const headerRefs = useRef<Map<string, HTMLDivElement>>(new Map())
	const headerElementsCache = useRef<Map<string, { titleEl: HTMLElement; dividerEl: HTMLElement }>>(new Map())
	const rafIdRef = useRef<number | null>(null)
	const lastTranslateRef = useRef(0)
	const isActiveRef = useRef(false)
	const isProgrammaticScrollRef = useRef(false)

	const updateStickyHeaders = useCallback(() => {
		const swiper = swiperRef.current
		if (!swiper?.slides.length || !swiper.wrapperEl) return

		const translate = getTranslateX(swiper.wrapperEl)
		const updates: StickyUpdate[] = []

		swiper.slides.forEach((slideEl, index) => {
			const month = months[index]
			if (!month) return

			const headerContainer = headerRefs.current.get(month.id)
			if (!headerContainer) return

			let cached = headerElementsCache.current.get(month.id)
			if (!cached) {
				const titleEl = headerContainer.querySelector('[data-sticky-title]') as HTMLElement
				const dividerEl = headerContainer.querySelector('[data-sticky-divider]') as HTMLElement
				if (!titleEl || !dividerEl) return
				cached = { titleEl, dividerEl }
				headerElementsCache.current.set(month.id, cached)
			}

			const { titleEl, dividerEl } = cached
			const slide = slideEl as HTMLElement
			const slideOffset = slide.offsetLeft
			const slideWidth = slide.offsetWidth
			const titleWidth = titleEl.offsetWidth

			const visibleLeft = slideOffset + translate
			const overscroll = Math.max(0, -visibleLeft)
			const maxOverscroll = Math.max(0, slideWidth - titleWidth - SLIDE_PADDING * 2 - HEADER_GAP)
			const clampedOverscroll = Math.min(overscroll, maxOverscroll)
			const newDividerWidth = Math.max(0, slideWidth - titleWidth - SLIDE_PADDING * 2 - HEADER_GAP - clampedOverscroll)

			updates.push({ titleEl, dividerEl, clampedOverscroll, newDividerWidth })
		})

		updates.forEach(({ titleEl, dividerEl, clampedOverscroll, newDividerWidth }) => {
			titleEl.style.transform = `translateX(${clampedOverscroll}px)`
			dividerEl.style.transform = `translateX(${clampedOverscroll}px)`
			dividerEl.style.width = `${newDividerWidth}px`
		})
	}, [months, swiperRef])

	const startRAF = useCallback(() => {
		isActiveRef.current = true
		if (rafIdRef.current !== null) return

		const loop = () => {
			const swiper = swiperRef.current?.wrapperEl
			if (!swiper) {
				rafIdRef.current = null
				return
			}

			const currentTranslate = getTranslateX(swiper)
			const translateChanged = Math.abs(currentTranslate - lastTranslateRef.current) > TRANSLATE_THRESHOLD
			lastTranslateRef.current = currentTranslate

			if (!isProgrammaticScrollRef.current) {
				updateStickyHeaders()
				emitCurrentMonth()
			}

			if (isActiveRef.current || translateChanged)
				rafIdRef.current = requestAnimationFrame(loop)
			else
				rafIdRef.current = null
		}

		rafIdRef.current = requestAnimationFrame(loop)
	}, [updateStickyHeaders, emitCurrentMonth])

	const stopRAF = useCallback(() => {
		isActiveRef.current = false
	}, [])

	const registerHeader = useCallback((monthId: string, el: HTMLDivElement | null) => {
		if (el)
			headerRefs.current.set(monthId, el)
		else {
			headerRefs.current.delete(monthId)
			headerElementsCache.current.delete(monthId)
		}
	}, [])

	return {
		updateStickyHeaders,
		startRAF,
		stopRAF,
		registerHeader,
		isProgrammaticScrollRef
	}
}
